import { Link } from 'react-router-dom';
import { 
  Shield, 
  Target, 
  Users, 
  Award,
  ArrowRight,
  Github,
  Twitter,
  Linkedin
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'We practice what we preach. Security is embedded in everything we do, from our curriculum to our infrastructure.',
    },
    {
      icon: Target,
      title: 'Practical Learning',
      description: 'Theory is important, but hands-on experience is essential. Our training emphasizes real-world skills.',
    },
    {
      icon: Users,
      title: 'Community Driven',
      description: 'We believe in the power of community. Learning together and sharing knowledge makes everyone stronger.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in everything - our content, our instruction, and our student outcomes.',
    },
  ];

  const team = [
    {
      name: 'Dhruv Shah',
      role: 'Co-Founder & Infosec Head',
      bio: '15+ years in offensive security Founder @ TCP Infosec. Former Director at Notsosecure Claranet.',
      avatar: '/images/DhruvShah.webp',
      social: {
        twitter: 'https://x.com/snypter',
        linkedin: 'https://www.linkedin.com/in/dhruvshahs',
        github: 'https://github.com/InfosecDhruv',
      },
    },
    {
      name: 'Harshit Mahajan',
      role: 'Co-Founder & Business Head',
      bio: '10+ years of experience in organizing Cybersecurity events.',
      avatar: '/images/harshitmahajan.webp',
      social: {
        twitter: 'https://x.com/HarshitRMahajan',
        linkedin: 'https://www.linkedin.com/in/harshitmahajan/',
        github: '#',
      },
    },
    {
      name: 'Anant Shrivastava',
      role: 'Advisor',
      bio: 'Founder @ Cyfinoid and and an expert in multiple domains of CyberSecurity.',
      avatar: '/images/AnantShri.webp',
      social: {
        twitter: 'https://x.com/anantshri',
        linkedin: 'https://www.linkedin.com/in/anantshri/',
        github: 'https://github.com/anantshri/',
      },
    },
  ];

  const milestones = [
    { year: '2025', event: 'Byt3con Academy founded with first online course event' },
    { year: '2026', event: 'Launched Byt3cast podcast, and co-hort sessions' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 right-1/4" />
        
        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                ./about_us
              </p>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-[var(--text-main)] mb-6">
                Training the Next Generation of
                <span className="text-[var(--primary)]"> Cyber Security Specialists</span>
              </h1>
              <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
                Byt3con Academy was founded with a simple mission: make high-quality 
                cybersecurity education accessible and affordable to everyone who wants to learn.
              </p>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="scanline-overlay rounded-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1670383050616-682df7d57b22?crop=entropy&cs=srgb&fm=webp&w=1200&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBkYXJrJTIwbW9kZXJufGVufDB8fHx8MTc3MDA2MTQwMnww&ixlib=rb-4.1.0&q=85"
                  alt="Byt3con Team"
                  className="w-full h-auto object-cover opacity-80"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[var(--primary)] px-6 py-3">
                <span className="font-mono text-black font-bold">Est. 2025</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-12 md:py-20 border-y border-[var(--border)]" data-testid="story-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              ./our_story
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)] mb-8">
              From Hackers to Educators
            </h2>
            <div className="space-y-6 text-[var(--text-muted)] text-lg leading-relaxed">
              <p>
                Byt3con Academy started in 2025 when a group of security professionals 
                realized that the traditional path into cybersecurity was broken. Expensive 
                bootcamps, outdated curricula, and access to talented people is limited leading to mediocre trainings.
              </p>
              <p>
                We decided to build something different. A place where practical, hands-on 
                skills were prioritized over certifications. Where community and collaboration
                replaced competition. Where anyone with the drive to learn & teach could access 
                world-class training programmes.
              </p>
              <p>
                Today, we've trained multiple participants across various sectors in the cybersecurity industry. Our participants 
                work at top security firms, tech giants, and government agencies. Our Trainers teach at prestigious conferences like BlackHat, Defcon and more.
                But we're just getting started.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 md:py-20" data-testid="values-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              ./our_values
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
              What We Stand For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-[var(--surface)] border border-[var(--border)] p-8 hover:border-[var(--primary)] transition-colors duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-[var(--surface-highlight)] border border-[var(--border)] flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-[var(--primary)]" />
                </div>
                <h3 className="font-mono text-xl font-bold text-[var(--text-main)] mb-4">
                  {value.title}
                </h3>
                <p className="text-[var(--text-muted)] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 md:py-20 border-y border-[var(--border)]" data-testid="timeline-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
                ./milestones
              </p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
                Our Journey
              </h2>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-[var(--border)] transform md:-translate-x-1/2" />

              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`relative flex items-center mb-12 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-testid={`milestone-${index}`}
                >
                  {/* Content */}
                  <div className={`ml-8 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <span className="font-mono text-[var(--primary)] text-2xl font-bold">
                      {milestone.year}
                    </span>
                    <p className="text-[var(--text-main)] mt-2">
                      {milestone.event}
                    </p>
                  </div>

                  {/* Dot */}
                  <div className="absolute left-0 md:left-1/2 w-4 h-4 bg-[var(--primary)] transform md:-translate-x-1/2 shadow-[0_0_10px_var(--glow-primary)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-12 md:py-20" data-testid="team-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-4">
              ./the_team
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)]">
              Meet the Hackers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-[var(--surface)] border border-[var(--border)] p-8 group hover:border-[var(--primary)] transition-colors duration-300"
                data-testid={`team-member-${index}`}
              >
                {/* Avatar */}
                {/* <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--primary)] flex items-center justify-center mb-6 font-mono text-2xl font-bold text-[var(--primary)]">
                  {member.avatar}
                </div> */}
                <div className="flex items-center gap-4">
                <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-xl object-cover" loading="lazy" decoding="async" width="64" height="64" />

                {/* Info */}
                <div>
                <h3 className="font-mono text-xl font-bold text-[var(--text-main)] mb-1">
                  {member.name}
                </h3>
                
                <p className="text-[var(--primary)] text-sm font-mono mb-4">
                  {member.role}
                </p>
                </div>
                </div>
                
                <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-6">
                  {member.bio}
                </p>

                {/* Social */}
                <div className="flex gap-3">
                  <a
                    href={member.social.twitter}
                    className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                    aria-label={`${member.name} Twitter`}
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                  <a
                    href={member.social.linkedin}
                    className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                    aria-label={`${member.name} LinkedIn`}
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a
                    href={member.social.github}
                    className="w-8 h-8 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors duration-200"
                    aria-label={`${member.name} GitHub`}
                  >
                    <Github className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 border-t border-[var(--border)] relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow bottom-0 left-1/2 -translate-x-1/2" />
        
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)] mb-6">
            Want to Join Us?
          </h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-10">
            We're always looking for talented individuals who are passionate about 
            security and education. Check out our open positions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[var(--primary)] text-black font-mono font-bold px-10 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_20px_var(--glow-primary)]"
              data-testid="about-contact-cta"
            >
              View Careers <ArrowRight className="w-5 h-5" />
            </Link> */}
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-[var(--primary)] text-[var(--primary)] font-mono px-10 py-4 uppercase tracking-widest hover:bg-[var(--primary)]/10 transition-colors duration-200"
              data-testid="about-partner-cta"
            >
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
